import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, X, AlertCircle, CheckCircle, Loader, Clock, ChevronDown, Zap, Terminal } from 'lucide-react';

const ORCHESTRATOR_BASE = 'http://localhost:8000';
const WS_BASE = 'ws://localhost:8000';

const PRIORITY_LABELS = {
  1: { label: 'Critical', color: 'text-red-400', bg: 'bg-red-900/30 border-red-500/30' },
  2: { label: 'High', color: 'text-orange-400', bg: 'bg-orange-900/30 border-orange-500/30' },
  3: { label: 'Normal', color: 'text-yellow-400', bg: 'bg-yellow-900/30 border-yellow-500/30' },
  4: { label: 'Low', color: 'text-blue-400', bg: 'bg-blue-900/30 border-blue-500/30' },
  5: { label: 'Minimal', color: 'text-slate-400', bg: 'bg-slate-900/30 border-slate-500/30' },
};

function TaskHistoryItem({ task }) {
  const pri = PRIORITY_LABELS[task.priority] || PRIORITY_LABELS[3];
  const ts = task.timestamp
    ? new Date(task.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '--';

  return (
    <div className="flex flex-col gap-1 p-3 rounded-lg bg-[#0a0e1a] border border-slate-700/40 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-[#7c3aed] truncate">{task.agent_name || task.agent || 'Orchestrator'}</span>
        <span className="text-slate-500 flex-shrink-0">{ts}</span>
      </div>
      <p className="text-slate-300 line-clamp-2">{task.task}</p>
      <div className="flex items-center gap-2 mt-0.5">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${pri.bg} ${pri.color}`}>
          P{task.priority} {pri.label}
        </span>
        {task.status === 'success' && (
          <span className="flex items-center gap-1 text-green-400 text-[10px]">
            <CheckCircle className="w-3 h-3" />
            Done
          </span>
        )}
        {task.status === 'error' && (
          <span className="flex items-center gap-1 text-red-400 text-[10px]">
            <AlertCircle className="w-3 h-3" />
            Failed
          </span>
        )}
        {task.status === 'pending' && (
          <span className="flex items-center gap-1 text-yellow-400 text-[10px]">
            <Loader className="w-3 h-3 animate-spin" />
            Running
          </span>
        )}
      </div>
      {task.response && (
        <div className="mt-1 pt-1 border-t border-slate-700/40 text-slate-400 line-clamp-3 font-mono">
          {task.response}
        </div>
      )}
    </div>
  );
}

export default function TaskPanel({ agents = [], selectedAgent = null, onClose }) {
  const [task, setTask] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState(selectedAgent?.id || '');
  const [priority, setPriority] = useState(3);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [streamingOutput, setStreamingOutput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [taskHistory, setTaskHistory] = useState([]);
  const wsRef = useRef(null);
  const outputRef = useRef(null);
  const taskIdRef = useRef(null);

  // Sync selected agent when prop changes
  useEffect(() => {
    if (selectedAgent?.id) {
      setSelectedAgentId(selectedAgent.id);
    }
  }, [selectedAgent?.id]);

  // Scroll streaming output to bottom
  useEffect(() => {
    if (outputRef.current && streamingOutput) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [streamingOutput]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const connectWebSocket = useCallback((taskId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = `${WS_BASE}/ws/task/${taskId}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsStreaming(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.chunk) {
            setStreamingOutput((prev) => prev + data.chunk);
          } else if (data.done) {
            setIsStreaming(false);
            setSubmitting(false);
            setTaskHistory((prev) => {
              const updated = [...prev];
              const idx = updated.findIndex((t) => t.task_id === taskId);
              if (idx !== -1) {
                updated[idx] = { ...updated[idx], status: 'success', response: data.result || streamingOutput };
              }
              return updated;
            });
          } else if (data.error) {
            setError(data.error);
            setIsStreaming(false);
            setSubmitting(false);
          }
        } catch {
          // Plain-text chunk
          setStreamingOutput((prev) => prev + event.data);
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection failed. Response may still be processing.');
        setIsStreaming(false);
        setSubmitting(false);
      };

      ws.onclose = () => {
        setIsStreaming(false);
      };
    } catch (err) {
      // WebSocket not available; fall back to polling or show output inline
      setError(`Could not connect to streaming endpoint: ${err.message}`);
      setIsStreaming(false);
      setSubmitting(false);
    }
  }, [streamingOutput]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!task.trim()) return;

    setSubmitting(true);
    setError(null);
    setStreamingOutput('');

    const targetAgent = agents.find((a) => a.id === selectedAgentId);
    const agentPort = targetAgent?.port;
    // Prefer direct-to-agent POST; fall back to orchestrator
    const endpoint = agentPort
      ? `http://localhost:${agentPort}/task`
      : `${ORCHESTRATOR_BASE}/task`;

    const payload = {
      task: task.trim(),
      priority,
      agent: selectedAgentId || null,
      agent_name: targetAgent?.name || null,
    };

    const historyEntry = {
      task_id: `local-${Date.now()}`,
      task: task.trim(),
      priority,
      agent: selectedAgentId,
      agent_name: targetAgent?.name,
      status: 'pending',
      timestamp: new Date().toISOString(),
      response: null,
    };
    setTaskHistory((prev) => [historyEntry, ...prev.slice(0, 9)]);
    taskIdRef.current = historyEntry.task_id;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      const taskId = result.task_id || result.id;

      // Update history entry with server task_id
      setTaskHistory((prev) => {
        const updated = [...prev];
        const idx = updated.findIndex((t) => t.task_id === historyEntry.task_id);
        if (idx !== -1) {
          updated[idx] = { ...updated[idx], task_id: taskId || updated[idx].task_id };
        }
        return updated;
      });

      if (taskId) {
        connectWebSocket(taskId);
      } else if (result.response || result.result) {
        // Synchronous response
        const resp = result.response || result.result;
        setStreamingOutput(resp);
        setSubmitting(false);
        setTaskHistory((prev) => {
          const updated = [...prev];
          if (updated.length > 0) {
            updated[0] = { ...updated[0], status: 'success', response: resp };
          }
          return updated;
        });
      } else {
        setSubmitting(false);
        setTaskHistory((prev) => {
          const updated = [...prev];
          if (updated.length > 0) {
            updated[0] = { ...updated[0], status: 'success' };
          }
          return updated;
        });
      }

      setTask('');
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
      setTaskHistory((prev) => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[0] = { ...updated[0], status: 'error' };
        }
        return updated;
      });
    }
  };

  const selectedPriority = PRIORITY_LABELS[priority] || PRIORITY_LABELS[3];
  const selectedAgentObj = agents.find((a) => a.id === selectedAgentId);

  return (
    <div className="flex flex-col h-full bg-[#0d1117] border border-[#7c3aed]/20 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/40 bg-[#0a0e1a]">
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-[#7c3aed]" />
          <h2 className="text-sm font-semibold text-white">Task Dispatch</h2>
          {selectedAgentObj && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#7c3aed]/20 text-[#7c3aed] border border-[#7c3aed]/30">
              {selectedAgentObj.name}
            </span>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
            aria-label="Close task panel"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4 border-b border-slate-700/40 space-y-3">
        {/* Agent selector */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Target Agent</label>
          <div className="relative">
            <select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="w-full appearance-none bg-[#0a0e1a] border border-slate-600/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#7c3aed]/50 focus:ring-1 focus:ring-[#7c3aed]/30 pr-8"
            >
              <option value="">CAO Orchestrator (auto-route)</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} — {a.role}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Priority selector */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Priority</label>
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map((p) => {
              const pri = PRIORITY_LABELS[p];
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  className={`
                    flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150
                    ${priority === p
                      ? `${pri.bg} ${pri.color} ring-1 ring-current`
                      : 'bg-slate-800/50 border-slate-700/50 text-slate-500 hover:text-slate-300'
                    }
                  `}
                  aria-pressed={priority === p}
                  aria-label={`Priority ${p}: ${pri.label}`}
                >
                  P{p}
                </button>
              );
            })}
          </div>
          <p className={`mt-1 text-xs ${selectedPriority.color}`}>
            {selectedPriority.label} priority
          </p>
        </div>

        {/* Task textarea */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Task Description</label>
          <textarea
            value={task}
            onChange={(e) => setTask(e.target.value)}
            placeholder="Describe the task for this agent..."
            rows={4}
            className="w-full bg-[#0a0e1a] border border-slate-600/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#7c3aed]/50 focus:ring-1 focus:ring-[#7c3aed]/30 resize-none font-mono"
            disabled={submitting}
          />
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-900/20 border border-red-500/30 text-red-400 text-xs">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={submitting || !task.trim()}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gradient-to-r from-[#7c3aed] to-[#6d28d9] text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:from-[#8b5cf6] hover:to-[#7c3aed] transition-all duration-200 shadow-lg shadow-[#7c3aed]/25"
        >
          {submitting ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              {isStreaming ? 'Streaming...' : 'Sending...'}
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Dispatch Task
            </>
          )}
        </button>
      </form>

      {/* Streaming output */}
      {streamingOutput && (
        <div className="p-4 border-b border-slate-700/40">
          <div className="flex items-center gap-2 mb-2">
            <Zap className={`w-4 h-4 ${isStreaming ? 'text-yellow-400 animate-pulse' : 'text-green-400'}`} />
            <span className="text-xs font-medium text-slate-400">
              {isStreaming ? 'Streaming response...' : 'Response'}
            </span>
          </div>
          <div
            ref={outputRef}
            className="max-h-40 overflow-y-auto bg-[#0a0e1a] rounded-lg p-3 text-xs text-green-300 font-mono whitespace-pre-wrap border border-slate-700/40 scrollbar-thin"
          >
            {streamingOutput}
            {isStreaming && <span className="animate-pulse text-[#7c3aed]">█</span>}
          </div>
        </div>
      )}

      {/* Task history */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-slate-500" />
          <h3 className="text-xs font-medium text-slate-400">Recent Tasks</h3>
          {taskHistory.length > 0 && (
            <span className="ml-auto text-xs text-slate-600">
              {taskHistory.length} / 10
            </span>
          )}
        </div>

        {taskHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Terminal className="w-8 h-8 text-slate-700 mb-2" />
            <p className="text-xs text-slate-600">No tasks dispatched yet.</p>
            <p className="text-xs text-slate-700 mt-1">Select an agent and enter a task above.</p>
          </div>
        ) : (
          taskHistory.map((t, i) => (
            <TaskHistoryItem key={t.task_id || i} task={t} />
          ))
        )}
      </div>
    </div>
  );
}
